import { indexedDbStorage } from './indexedDbStorage'

/**
 * Records left behind by caches that no longer exist.
 *
 * The streams cache moved localStorage -> IndexedDB, then gained a per-tenant key suffix. Each move
 * started writing somewhere new without removing the old record, so a browser that has been through
 * both still carries two payloads that nothing reads — one of them a full set of stream schemas
 * from whichever project happened to be open at the time.
 *
 * The four settings caches stopped being persisted entirely: their pages fetch on mount, so the
 * stored copy only ever produced a stale first paint, and the users record additionally kept
 * `isLoading` and `error` across reloads. The general settings record only copied `/v3/me` data that
 * the auth record already holds.
 */
const LEGACY_LOCAL_STORAGE_KEYS = [
	'streams-storage',
	'roles-settings-storage',
	'users-settings-storage',
	'invoice-storage',
	'dbc-parser-storage',
	'general-settings-storage',
]
const LEGACY_INDEXED_DB_KEYS = ['streams-storage']

/**
 * Records that the sweep already ran, so it doesn't reopen IndexedDB on every future boot. Bumped
 * when the key list grows, since a browser that ran the previous sweep would otherwise skip it.
 */
const CLEANUP_MARKER_KEY = 'cache-cleanup-done-v3'

/**
 * Delete them, once, at startup. Best-effort: this only reclaims space, so any failure is ignored
 * rather than surfaced. Safe to run repeatedly — removing an absent key is a no-op — but the marker
 * keeps it to a single sweep per browser instead of an IndexedDB open on every load, for the
 * overwhelming majority of users who have nothing left to clean.
 */
export async function clearLegacyStreamCaches(): Promise<void> {
	try {
		if (localStorage.getItem(CLEANUP_MARKER_KEY)) {
			return
		}
	} catch {
		// Storage unavailable; fall through and attempt the sweep anyway.
	}

	for (const key of LEGACY_LOCAL_STORAGE_KEYS) {
		try {
			localStorage.removeItem(key)
		} catch {
			// Storage disabled or unavailable; nothing to reclaim.
		}
	}

	await Promise.all(
		LEGACY_INDEXED_DB_KEYS.map(key =>
			// Unsuffixed, so it can never collide with a live `<name>::<scope>` record.
			Promise.resolve(indexedDbStorage.removeItem(key)).catch(() => {
				// Already logged by the storage layer.
			}),
		),
	)

	try {
		localStorage.setItem(CLEANUP_MARKER_KEY, '1')
	} catch {
		// Couldn't record it; the sweep simply runs again next boot.
	}
}
